import { noteTeamInvited } from "src/Pages/KnowledgeBase/kbSession";
import React, { useEffect, useState } from "react";
import InviteModal from "./InviteModal/invitemodal";
import BranchService from "src/Services/API/branch.service";
import { toast } from "react-toastify";
import UserService from "src/Services/API/user.service";
import InviteService from "src/Services/API/invite.service";
import RoleService from "src/Services/API/role.service";
import { formatDate } from "src/Common/common";
import AcceptStatus from "./Status/acceptstatus";
import PendingStatus from "./Status/pendingstatus";
import ConfirmDialog from "src/Common/confirmdialog";
import { useSelector } from "react-redux";
import { can, isAdmin } from "src/Common/function";
import { useSearchParams } from "react-router-dom";

const Invite = ({ inviteModalShown, setInviteModalShown }) => {
  // const branch = useSelector((state) => state.Branch.branch);
  let currentUser = UserService.getCurrentUser();
  const [branchArray, setBranchArray] = useState([]);
  const [roleArray, setRoleArray] = useState([]);
  const [inviteArray, setInviteArray] = useState([]);

  const [cancelIndex, setCancelIndex] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  // Per-invite resend cooldown: { [inviteId]: epochMsWhenAllowedAgain }. `now`
  // ticks every second so the countdown label re-renders.
  const [cooldownUntil, setCooldownUntil] = useState({});
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const RESEND_COOLDOWN_MS = 60000;

  // The cooldown deadline for an invite, taking the later of: the server's
  // last-send time (invite.updatedAt + window, refetched on every mount so it
  // survives navigation/reload) and any local cooldown we just started.
  const getCooldownUntil = (invite) => {
    const fromServer = invite.updatedAt ? new Date(invite.updatedAt).getTime() + RESEND_COOLDOWN_MS : 0;
    const fromLocal = cooldownUntil[invite.id] || 0;
    return Math.max(fromServer, fromLocal);
  };

  const [searchParams] = useSearchParams();
  const branch = Number(searchParams.get("branch"));

  useEffect(() => {
    BranchService.getAll()
      .then((response) => {
        console.log("Role list: ", response);
        if (response.status == 200) {
          // The backend already returns only this company's branches. Admins/
          // accountants can invite to any of them; other roles are limited to
          // the branches they belong to. (A company's first admin is invited
          // with no branch, so filtering by their own branches would leave the
          // dropdown empty.)
          const branches = isAdmin()
            ? response.data
            : response.data.filter((b) => (currentUser.branches || []).some((b2) => b2.id == b.id));
          setBranchArray(branches);
        }
      })
      .catch((error) => {
        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });

    RoleService.getAll()
      .then((response) => {
        console.log("Role list: ", response);
        if (response.status == 200) {
          setRoleArray(response.data);
        }
      })
      .catch((error) => {
        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });

    getAllInvites();
  }, [branch]);

  const getAllInvites = () => {
    InviteService.getAll(branch)
      .then((response) => {
        console.log("Invite list: ", response);
        if (response.status == 200) {
          setInviteArray(response.data);
        }
      })
      .catch((error) => {
        toast("An error occured");
      });
  };

  const sendInvite = (email, role, branch) => {
    console.log("🚀 ~ sendInvite ~ branch:", branch);
    InviteService.create({
      invitedBy: currentUser,
      email: email.trim().toLowerCase(),
      role,
      branch,
      company: currentUser.company,
    })
      .then((response) => {
        noteTeamInvited();
        getAllInvites();
        toast("User invited successfully");
      })
      .catch((error) => {
        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });

    setInviteModalShown(false);
  };

  const resendInvite = (index) => {
    const invite = inviteArray[index];
    if (!invite || resendingId === invite.id) return;
    if (getCooldownUntil(invite) > Date.now()) return;
    setResendingId(invite.id);
    InviteService.resend(invite.id)
      .then(() => {
        toast("Invitation resent successfully");
        // Start the 60s cooldown for this invite (backend enforces it too).
        setCooldownUntil((prev) => ({ ...prev, [invite.id]: Date.now() + 60000 }));
      })
      .catch((error) => {
        const data = error.response?.data;
        // If we hit the server cooldown, start the countdown from the remaining
        // time it reports so the user sees how long to wait.
        if (error.response?.status === 429 && data?.retryAfter) {
          setCooldownUntil((prev) => ({
            ...prev,
            [invite.id]: Date.now() + data.retryAfter * 1000,
          }));
        }
        toast(data?.message || "An error occured");
      })
      .finally(() => {
        setResendingId(null);
      });
  };

  const confirmCancelInvite = () => {
    if (cancelIndex === null) return;
    InviteService.deleteInvite(inviteArray[cancelIndex].id)
      .then((response) => {
        getAllInvites();
        toast("Invite cancelled successfully");
      })
      .catch((error) => {
        toast("An error occured");
      })
      .finally(() => {
        setCancelIndex(null);
      });
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <ConfirmDialog
        title="CANCEL INVITE"
        description="Are you sure you want to cancel this invite?"
        shown={cancelIndex !== null}
        onConfirmClicked={confirmCancelInvite}
        onCancelClicked={() => setCancelIndex(null)}
      />
      <InviteModal
        sendInvite={sendInvite}
        shown={inviteModalShown}
        roleArray={roleArray}
        branchArray={branchArray}
        onCancelClicked={() => setInviteModalShown(false)}
      />
      <div
        className="w-full mt-3 mb-3 flex-1 min-h-0 flex flex-col overflow-hidden"
        style={{
          borderRadius: "12px",
          border: "1px solid #E5E8F3",
          background: "#FFF",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden" data-tour="team-invites">
          <table className="w-full table-fixed">
            <thead className="sticky top-0 z-10">
              <tr style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}>
                <th className="w-[30%]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    <span>Email</span>
                  </div>
                </th>
                <th className="w-[20%]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    Role
                  </div>
                </th>
                <th className="w-[25%]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    Status
                  </div>
                </th>
                <th className="w-[25%]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    Invited At
                  </div>
                </th>
                <th className="w-[170px]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    Action
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {inviteArray.map((invite, i) => (
                <tr key={i} className="border-b border-borderColor hover:bg-[#FAFAFB]">
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[14px] font-[400] leading-[20px] text-[#5F6477] truncate">
                      {invite.email}
                    </div>
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[14px] font-[400] leading-[20px] text-[#5F6477]">
                      {invite.role.name}
                    </div>
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[14px] font-[400] leading-[20px] text-[#5F6477]">
                      {invite.accepted ? <AcceptStatus /> : <PendingStatus />}
                    </div>
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[14px] font-[400] leading-[20px] text-[#5F6477]">
                      {formatDate(invite.createdAt)}
                    </div>
                  </td>
                  <td>
                    <div className="px-4 py-3 flex items-center gap-2">
                        <div
                          onClick={() => resendInvite(i)}
                          className={`w-[70px] h-[25px] text-[12px] rounded-[6px] border-[1px] border-blueColor flex items-center justify-center text-blueColor ${
                            resendingId === invite.id || getCooldownUntil(invite) > now
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        >
                          {getCooldownUntil(invite) > now
                            ? `${Math.ceil((getCooldownUntil(invite) - now) / 1000)}s`
                            : "RESEND"}
                        </div>
                      {can("user:cancelInvite") && (
                        <div
                          onClick={() => setCancelIndex(i)}
                          className="w-[70px] h-[25px] text-[12px] rounded-[6px] border-[1px] border-subTitleColor flex items-center justify-center text-subTitleColor cursor-pointer"
                        >
                          CANCEL
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Invite;
